import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase";
import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Spinner } from "flowbite-react";
import { Calendar, Clock, Video, User, ArrowRight, CreditCard, Sparkles, Users, MapPin } from "lucide-react";
import { NoCreditModal, ConfirmRoomEntryModal } from "./CreditModals";
import AppHeader from "./AppHeader";
import { getUserNameFromRoomId, getParticipantCount } from "../utils/roomUtils";
import { useAuth } from "./AuthContext";
import { useUserParticipations } from "../hooks/useUserParticipations";
import PublicSessionsList, { PublicAppointment } from "./PublicSessionsList";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import SessionCardSkeleton from "./SessionCardSkeleton";
import { goToApp } from "../utils/crossDomainNav";
import { isLiveKitOpenTestRoom } from "../utils/livekitOpenTestRoom";

interface PublicRoom {
  id: string;
  name?: string;
  createdBy: string;
  createdByName?: string;
  participantCount: number;
  createdAt: Date;
  isActive: boolean;
  public: boolean;
}

const getInitials = (name?: string) => {
  if (!name) return "R";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const OpenRooms: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<PublicAppointment[]>([]);
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schedulingAppointment, setSchedulingAppointment] = useState<string | null>(null);
  const [schedulingError, setSchedulingError] = useState<{ [key: string]: string }>({});
  const [schedulingSuccess, setSchedulingSuccess] = useState<{ [key: string]: boolean }>({});
  const [userParticipations, addParticipation] = useUserParticipations(user?.uid);
  const [participationCredits, setParticipationCredits] = useState<number>(0);
  const [showNoCreditModal, setShowNoCreditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [isEnteringRoom, setIsEnteringRoom] = useState(false);

  useEffect(() => {
    let unsubscribeRooms: (() => void) | null = null;

    const fetchAppointments = async () => {
      try {
        const now = new Date();
        const baseFilter = [where("dataSessao", ">=", now), where("isPublic", "==", true), where("status", "==", "confirmado")];
        const appointmentsQuery1 = query(collection(db, "appointments"), where("isPublic", "==", true), ...baseFilter);
        const appointmentsQuery2 = query(collection(db, "appointments"), where("permiteParticipantes", "==", true), ...baseFilter);

        const [appointmentsSnapshot1, appointmentsSnapshot2] = await Promise.all([
          getDocs(appointmentsQuery1),
          getDocs(appointmentsQuery2),
        ]);

        const appointmentsMap = new Map<string, PublicAppointment>();

        for (const docSnap of [...appointmentsSnapshot1.docs, ...appointmentsSnapshot2.docs]) {
          if (appointmentsMap.has(docSnap.id)) continue;

          const data = docSnap.data();
          let scheduleDate = data.dataSessao.toDate();
          if (!scheduleDate) {
            scheduleDate = new Date(data.dataAgendamento);
            const scheduleTime = data.horarioAgendamento?.split(":") || ["00", "00"];
            scheduleDate.setHours(parseInt(scheduleTime[0], 10));
            scheduleDate.setMinutes(parseInt(scheduleTime[1], 10));
          }

          const participantCount = typeof data.participantCount === "number" ? data.participantCount : 0;

          appointmentsMap.set(docSnap.id, {
            id: docSnap.id,
            therapistName: data.terapeutaNomePublico || data.terapeutaNome || "",
            therapistId: data.terapeutaId,
            patientName: data.pacienteNome,
            patientId: data.pacienteId,
            description: data.description,
            date: scheduleDate,
            duration: data.sessionDuration || 60,
            status: data.status === "confirmado" ? ("scheduled" as const) : (data.status as "scheduled" | "in-progress" | "completed"),
            roomId: data.roomId,
            public: data.isPublic || data.permiteParticipantes,
            expectedParticipants: participantCount,
          });
        }

        setAppointments(Array.from(appointmentsMap.values()));
      } catch (err: unknown) {
        console.error("Erro ao buscar appointments:", err);
        setError((err as Error)?.message || "Erro ao carregar appointments.");
      } finally {
        setAppointmentsLoading(false);
      }
    };

    const setupRoomsSnapshot = () => {
      const roomsQuery = query(collection(db, "rooms"), where("isPublic", "==", true), where("isActive", "==", true));

      unsubscribeRooms = onSnapshot(
        roomsQuery,
        async (snapshot) => {
          const roomsPromises = snapshot.docs
            // Sala de teste LiveKit não deve aparecer para participantes.
            .filter((docSnapshot) => !isLiveKitOpenTestRoom(docSnapshot.id))
            .map(async (docSnapshot) => {
            const data = docSnapshot.data();

            let createdByName = data.createdByName;
            if (!createdByName && data.createdBy) {
              createdByName = await getUserNameFromRoomId(`sala-${data.createdBy}`);
            }
            if (!createdByName) {
              createdByName = await getUserNameFromRoomId(docSnapshot.id);
            }
            // Evita card genérico "Terapeuta" sem dono identificável.
            if (!createdByName || createdByName === "Terapeuta") {
              return null;
            }

            const participantCount = await getParticipantCount(docSnapshot.id);

            return {
              id: docSnapshot.id,
              name: data.name,
              createdBy: data.createdBy,
              createdByName,
              participantCount,
              createdAt: data.sessionStartedAt?.toDate() || new Date(),
              isActive: data.isActive,
              public: data.public,
            } as PublicRoom;
          });

          const allRooms = (await Promise.all(roomsPromises)).filter(
            (room): room is PublicRoom => Boolean(room),
          );
          const roomsList = allRooms.filter((room) => room.participantCount > 0);
          setRooms(roomsList);
          setRoomsLoading(false);
        },
        (err) => {
          console.error("Erro no snapshot das salas:", err);
          setError(err?.message || "Erro ao monitorar salas ao vivo.");
          setRoomsLoading(false);
        }
      );
    };

    fetchAppointments();
    setupRoomsSnapshot();

    return () => {
      if (unsubscribeRooms) {
        unsubscribeRooms();
      }
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setParticipationCredits(0);
      return;
    }

    const fetchParticipationCredits = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const credits = userData?.creditBalances?.sessions?.participation || 0;
          setParticipationCredits(credits);
        }
      } catch (error) {
        console.error("Erro ao buscar créditos de participação:", error);
      }
    };

    fetchParticipationCredits();
  }, [user?.uid]);

  const handleEnterRoom = (roomId: string) => {
    if (participationCredits > 0) {
      setSelectedRoomId(roomId);
      setShowConfirmModal(true);
    } else {
      setShowNoCreditModal(true);
    }
  };

  const confirmEnterRoom = async () => {
    if (!selectedRoomId || !user?.uid) return;

    setIsEnteringRoom(true);

    try {
      const consumeCredit = httpsCallable(functions, "consumeParticipationCreditForRoom");
      const result = await consumeCredit({ roomId: selectedRoomId });

      const isCreator = (result.data as { isCreator?: boolean })?.isCreator;
      if (!isCreator) {
        setParticipationCredits((prev) => Math.max(0, prev - 1));
      }

      setShowConfirmModal(false);
      goToApp(`/sala/${selectedRoomId}`, navigate);
    } catch (error: unknown) {
      console.error("Erro ao entrar na sala:", error);
      setShowConfirmModal(false);
      const fb = error as { message?: string; code?: string; details?: unknown };
      const errorMessage =
        (typeof fb?.message === "string" && fb.message && !fb.message.startsWith("INTERNAL")
          ? fb.message
          : null) ||
        "Erro ao processar entrada na sala. Confira se a sala está pública e se você tem crédito de participação.";
      alert(errorMessage);
    } finally {
      setIsEnteringRoom(false);
      setSelectedRoomId(null);
    }
  };

  const handleScheduleParticipation = async (appointmentId: string) => {
    if (!user?.uid) {
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: "Você precisa estar logado para agendar participação." }));
      return;
    }

    if (userParticipations.has(appointmentId)) {
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: "Você já está registrado nesta sessão." }));
      return;
    }

    setSchedulingAppointment(appointmentId);
    setSchedulingError((prev) => ({ ...prev, [appointmentId]: "" }));
    setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: false }));

    try {
      const consumeTicket = httpsCallable(functions, "consumeRepresentativeTicket");
      await consumeTicket({ appointmentId });

      setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: true }));
      addParticipation(appointmentId);

      setTimeout(() => {
        setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: false }));
      }, 3000);
    } catch (error: unknown) {
      console.error("Erro ao agendar participação:", error);
      const errorMessage = (error as Error).message || "Erro ao agendar participação. Tente novamente.";
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: errorMessage }));
    } finally {
      setSchedulingAppointment(null);
    }
  };

  return (
    <>
      <AppHeader />

      <main className="min-h-screen bg-[#F3F6F1] pt-24 pb-12 sm:pt-28 sm:pb-16">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 sm:px-6">
          {/* Hero */}
          <section className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#2E4B4C] via-[#27413F] to-[#1F3637] shadow-[0_30px_90px_rgba(46,75,76,0.30)] ring-1 ring-white/10">
            {/* Glows decorativos */}
            <div className="pointer-events-none absolute -right-16 -top-20 h-72 w-72 rounded-full bg-[#A56096]/30 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-[#7FB8A3]/20 blur-3xl" aria-hidden="true" />
            {/* Textura pontilhada */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,_#ffffff_1px,_transparent_0)] [background-size:22px_22px]"
              aria-hidden="true"
            />

            <div className="relative flex min-h-[300px] flex-col justify-center px-6 py-12 sm:px-10 lg:px-14">
              <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#CDE4D9] backdrop-blur">
                <Sparkles className="h-3.5 w-3.5 text-[#E7B7D6]" aria-hidden="true" />
                Agendar
              </span>

              <h1 className="mt-5 max-w-2xl text-4xl font-bold leading-[1.05] tracking-tight text-[#F8F5ED] sm:text-5xl lg:text-6xl">
                Salas{" "}
                <span className="bg-gradient-to-r from-[#E7B7D6] to-[#C98BB6] bg-clip-text text-transparent">
                  Abertas
                </span>
              </h1>

              <p className="mt-5 max-w-xl text-base leading-7 text-[#D9E5E0] sm:text-lg">
                Participe de uma sessão ao vivo ou agende a sua própria constelação familiar. Tudo
                online, com a mesma profundidade do presencial.
              </p>

              {/* Pills */}
              <div className="mt-7 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-[#EAF3EE] ring-1 ring-white/10 backdrop-blur">
                  <Video className="h-4 w-4 text-[#9FD8C2]" aria-hidden="true" /> Sessões ao vivo
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-[#EAF3EE] ring-1 ring-white/10 backdrop-blur">
                  <Users className="h-4 w-4 text-[#9FD8C2]" aria-hidden="true" /> Constele ou represente
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-[#EAF3EE] ring-1 ring-white/10 backdrop-blur">
                  <MapPin className="h-4 w-4 text-[#9FD8C2]" aria-hidden="true" /> Para todo o Brasil
                </span>
              </div>
            </div>
          </section>

          {/* CTA planos */}
          <section className="rounded-[1.5rem] border border-[#D5E1DB] bg-white/95 p-5 shadow-[0_14px_35px_rgba(58,88,89,0.08)] sm:p-7">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A56096]">Agendar</p>
                <h3 className="mt-1 text-2xl font-bold text-[#3A5859]">Garanta o seu acesso para Constelar ou Participar</h3>
                <p className="mt-2 max-w-xl text-sm leading-6 text-[#5A7272] sm:text-base">
                  Adquira seu plano com poucos cliques e participe das sessões como representante ou constelado com segurança.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/planos?secao=participante")}
                className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#A56096] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(165,96,150,0.25)] transition-colors hover:bg-[#8E4D80] sm:w-auto"
              >
                <CreditCard className="h-4 w-4" aria-hidden="true" />
                Garanta o seu acesso para Constelar ou Participar
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </section>

          {error && (
            <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-6 text-center text-red-700" role="alert">
              {error}
            </p>
          )}

          {/* Sessões ao vivo */}
          <section>
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#4A7C75]">
                <Video className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-semibold text-[#3A5859]">Sessões ao vivo</h2>
            </div>

            {roomsLoading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <SessionCardSkeleton key={i} />
                ))}
              </div>
            ) : rooms.length === 0 ? (
              <div className="rounded-[1.75rem] border border-dashed border-[#D5E1DB] bg-white/80 px-6 py-12 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E8EFEB]">
                  <Video className="h-7 w-7 text-[#4A7C75]" aria-hidden="true" />
                </div>
                <p className="mt-4 text-sm text-[#5A7272]">Nenhuma sessão ao vivo disponível no momento.</p>
                <p className="mt-2 text-xs text-[#85A7A8]">Em breve novas sessões estarão disponíveis</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rooms.map((room) => (
                  <article
                    key={room.id}
                    className="flex h-full flex-col rounded-[1.75rem] border border-[#E8EFEB] bg-white p-6 shadow-[0_18px_40px_rgba(58,88,89,0.1)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_50px_rgba(58,88,89,0.14)]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#4A7C75] text-sm font-bold text-white">
                          {getInitials(room.createdByName)}
                        </div>
                        <h3 className="truncate font-semibold text-[#3A5859]">{room.name || room.createdByName}</h3>
                      </div>
                      <span className="shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        Ativa
                      </span>
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-center text-sm text-[#5A7272]">
                        <User className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                        {room.participantCount} participante{room.participantCount !== 1 ? "s" : ""}
                      </div>
                      <div className="flex items-center text-sm text-[#5A7272]">
                        <Clock className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                        Iniciada em: {formatDate(room.createdAt)}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#A56096] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#8E4D80] disabled:opacity-60"
                      onClick={() => handleEnterRoom(room.id)}
                      disabled={isEnteringRoom && selectedRoomId === room.id}
                    >
                      {isEnteringRoom && selectedRoomId === room.id ? (
                        <>
                          <Spinner size="sm" aria-label="Entrando na sala" />
                          Entrando...
                        </>
                      ) : (
                        <>
                          <Video className="h-4 w-4" aria-hidden="true" />
                          Entrar na Sala
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>

          {/* Sessões futuras */}
          <section>
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6C4E78]">
                <Calendar className="h-5 w-5 text-white" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-semibold text-[#3A5859]">Sessões futuras</h2>
            </div>

            <PublicSessionsList
              appointments={appointments}
              isLoading={appointmentsLoading}
              userParticipations={userParticipations}
              schedulingAppointment={schedulingAppointment}
              schedulingError={schedulingError}
              schedulingSuccess={schedulingSuccess}
              onScheduleParticipation={handleScheduleParticipation}
              emptyMessage="Nenhuma sessão futura disponível no momento."
              currentUserId={user?.uid}
              participationCredits={participationCredits}
              onViewPlans={() => navigate("/planos?secao=participante")}
            />
          </section>
        </div>

        <NoCreditModal
          isOpen={showNoCreditModal}
          onClose={() => setShowNoCreditModal(false)}
          onViewPlans={() => navigate("/planos?secao=participante")}
          type="room"
        />

        <ConfirmRoomEntryModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedRoomId(null);
          }}
          onConfirm={confirmEnterRoom}
          isProcessing={isEnteringRoom}
          participationCredits={participationCredits}
        />
      </main>
    </>
  );
};

export default OpenRooms;
